<template>
  <div class="recurrent-invoices-wrap">
    <div v-b-toggle.collapse-recurrent class="recurrent-invoices-toggle">
      <span class="when-open"><font-awesome-icon icon="fa-solid fa-square-minus" /></span>
      <span class="when-closed"><font-awesome-icon icon="fa-solid fa-square-plus" /></span>
      {{ $gettext('You currently have') }} {{ recurrentInvoicesCount }} {{ $gettext('scheduled invoices') }}
    </div>
    <b-collapse id="collapse-recurrent" class="recurrent-invoices-collapse">
      <b-table 
        :class="['recurrent-invoices-table gk-table gk-table--headed']"
        :items="recurrentInvoices" 
        :fields="recurrentInvoicesFields"
        :key="tableKey"
        @row-clicked="rowClicked"
        show-empty
      >
        <template #cell(name)="data">
          <span v-if="data.item.name">{{ data.value }}</span>
          <span v-else>Task for {{ data.item.invoice.number }}</span>
        </template>

        <template #cell(next_date)="data">
          {{ data.item.next_date }}
        </template>

        <template #cell(iterations_left)="data">
          <span v-if="data.item.end_method !== 'endless'">{{ data.item.iterations_left }}</span>
          <span v-else>{{ $gettext('No end') }}</span>
        </template>

        <template #cell(recipient_customer)="data">
          {{ data.item.recipient_customer_name }}
        </template>

        <template #cell(currency_total_amount)="data">
          € {{ data.item.invoice.total_amount | formatNumber }}
        </template>

        <template #cell(actions)="data">
          <div>
            <GekkoButton 
              icon="fa-solid fa-trash-can"
              class="text-danger"
              size="sm"
              variant="link"
              v-gk-confirm="{
                title: $gettext('Delete recurring invoice?'),
                message: $gettext('Are you sure you want to delete this invoice?'),
                options: {
                  variant: 'danger',
                  confirmText: $gettext('Delete'),
                },
                callback: deleteRecurrentInvoice,
                callbackParams: data.item
              }"
            />
          </div>
        </template>

        <template #empty="">
          {{ $gettext('You don\'t have any scheduled invoices yet.') }}
        </template>
      </b-table>
    </b-collapse>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import EmptyState from "@/components/ui/EmptyState"
import GekkoButton from "@/components/GekkoButton";

export default {
  name: "RecurrentInvoicesTable",
  components: {
    EmptyState,
    GekkoButton,
  },
  data () {
    return {
      recurrentInvoices: [],
      recurrentCustomers: [],
      tableKey: 0,
      filters: { 
        extended: 'True',
        active: 'True',
        company_profile: null
      },
      recurrentInvoicesFields: [
        { key: 'name', label: this.$gettext('Name'), tdClass: '' },
        { key: 'next_date', label: this.$gettext('Next delivery'), tdClass: '' },
        { key: 'iterations_left', label: this.$gettext('Instances left'), tdClass: '' },
        { key: 'recipient_customer', label: this.$gettext('Customer'), tdClass: '' },
        { key: 'currency_total_amount', label: this.$gettext('Amount'), tdClass: '' },
        { key: 'actions', label: '', tdClass: '' },
      ],
    }
  },
  computed: {
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
      getCompanyId: 'main/getCompanyId',
      getCustomerById: 'customers/getCustomerById'
    }),
    recurrentInvoicesCount () {
      return (this.recurrentInvoices) ? this.recurrentInvoices.length : 0
    }
  },
  created () {
    this.filters.company_profile = this.getCompanyId
    this.loadRecurrentTasks()
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'invoices/REFRESH_INVOICE_RECURRENT_LIST') {
        this.loadRecurrentTasks()
      }
    })
  },
  destroyed () {
    this.unsubscribe()
  },
  methods: {
    ...mapActions({ 
      retrieveCustomer: 'customers/retrieveCustomer'
    }),
    rowClicked (item) {
      this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: item.id }, query: { scheduler: true } }).catch(() => {})
    },
    rowClass (item) {
      if(item && item.type) return 'recurrent-row-' + item.type + ' ' + 'recurrent-row-' + item.status
    },
    deleteInvoiceTableItem (item) {
      for (var i = 0; i < this.recurrentInvoices.length; i++) {
        if (this.recurrentInvoices[i].id === item.id) {
          this.recurrentInvoices.splice(i, 1)
          break;
        }
      }
      this.tableKey++
    },
    async loadRecurrentTasks () {
      try {
        const response = await InvoiceRecurrentService.list(this.filters);
        this.recurrentInvoices = response.data;
        await this.fetchCustomerNames();
      } catch (error) {
        console.error("Error loading recurrent invoices:", error);
      }
    },
    async fetchCustomerNames() {
      for (let recurrent_invoice of this.recurrentInvoices) {
        const recipient_customer = await this.getRecipientCustomerName(recurrent_invoice.invoice.recipient_customer)
        this.$set(recurrent_invoice, 'recipient_customer_name', recipient_customer.name)
      }
    },
    async getRecipientCustomerName (customer_id) {
      let customer = this.getCustomerById('recurrent', customer_id)
      if (customer) return customer
      try {
        const customer = await this.retrieveCustomer({ type: 'recurrent', customerId: customer_id })
        if (customer) return customer
      } catch (error) {
        console.error('Failed to fetch customer:', error)
        return null
      }
    },
    deleteRecurrentInvoice (invoice) {
      const delete_invoice = Object.assign({}, invoice)
      delete_invoice.active = false
      delete_invoice.company_profile = this.currentCompany.id;
      delete_invoice.created_by = invoice.created_by.id;
      if (invoice.days.length == 0) {
        delete_invoice.days = ''
      } else {
        delete_invoice.days = invoice.days.join()
      }
      delete_invoice.invoice = invoice.invoice.id
      InvoiceRecurrentService.update(delete_invoice.id, delete_invoice).then((response) => {
        this.deleteInvoiceTableItem(response.data)
      })
    }
  }
}
</script>

<style lang="scss">
.recurrent-invoices-wrap {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  background: #fcf3d9;
  border-radius: $radiusMd;
  overflow: hidden;
  .recurrent-invoices-toggle {
    padding: 0.5rem;
    font-size: 14px;
    color: #d6a00f;
    user-select: none;
    &.collapsed > .when-open,
    &.not-collapsed > .when-closed {
      display: none;
    }
    .when-open, .when-closed { margin-right: 0.5rem; }
  }
  .recurrent-invoices-collapse {
    background: #fcf3d9;
  }
  .recurrent-invoices-table {
    background: #fef9ec;
    font-size: 14px;
    thead {
      background: #fcf3d9;
      text-transform: none;
      color: $colorTextBase;
    }
    tbody {
      tr {
        &:hover {
          background: #e8f3de;
        }
      }
    }
  }
}
</style>